import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Box, Typography, useTheme } from '@mui/material';

import LoadingButton from '@mui/lab/LoadingButton';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import ControlledPasswordField from '@frontend/features/common/components/ControlledPasswordField';

import { useAuth } from '../hooks/useAuth';
import { ChangePasswordValues } from './AuthContext';

interface Props {
  switchToForm: () => void;
}

type FormValues = Pick<ChangePasswordValues, 'currentPassword' | 'newPassword' | 'confirmNewPassword'>;

const resolver = yupResolver(
  yup.object().shape({
    currentPassword: yup.string().required(),
    newPassword: yup.string().required(),
    confirmNewPassword: yup
      .string()
      .oneOf([yup.ref('newPassword')], 'auth.changePassword.passwordsDontMatch')
      .required('common.form.required'),
  }),
);

export const ChangePassword: FC<Props> = ({ switchToForm }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { changePassword } = useAuth();
  const { addToast } = useSnackBar();

  const methods = useForm<FormValues>({
    resolver,
  });
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = methods;
  const [loading, setLoading] = useState(false);

  const onSubmit = async (data: FormValues) => {
    setLoading(true);
    changePassword({
      ...data,
      callback: (status, code) => {
        if (status === 'success') {
          addToast({
            color: 'success',
            message: t('auth.changePassword.success'),
          });
        } else if (code === 400) {
          addToast({
            color: 'error',
            message: t('auth.changePassword.passwordsDontMatch'),
          });
        } else if (code === 0) {
          addToast({
            color: 'error',
            message: t('common.error.client'),
          });
        } else if (code === 401) {
          addToast({
            color: 'error',
            message: t('auth.changePassword.invalidPassword'),
          });
        } else {
          addToast({
            color: 'error',
            message: t('common.error.server'),
          });
        }
        setLoading(false);
      },
    });
  };

  return (
    <Box display="flex" flexDirection="column" justifyContent="space-around" padding={2} minWidth={400}>
      <Typography variant="h6" mb={theme.spacing(4)}>
        {t('auth.changePassword.title')}
      </Typography>
      <Box
        component="form"
        display="flex"
        flexDirection="column"
        gap={theme.spacing(2)}
        onSubmit={handleSubmit(onSubmit)}
      >
        <ControlledPasswordField
          required
          fullWidth
          control={control}
          name="currentPassword"
          size="small"
          placeholder={t('auth.changePassword.currentPassword')}
          errorMessage={t('common.form.required')}
        />

        <ControlledPasswordField
          required
          fullWidth
          control={control}
          name="newPassword"
          size="small"
          placeholder={t('auth.changePassword.newPassword')}
          errorMessage={t('common.form.required')}
        />

        <ControlledPasswordField
          required
          fullWidth
          control={control}
          name="confirmNewPassword"
          size="small"
          placeholder={t('auth.changePassword.confirmNewPassword')}
          errorMessage={errors.confirmNewPassword && t(`${errors.confirmNewPassword.message}`)}
        />

        <Box display="flex" justifyContent="flex-end" gap={theme.spacing(1)} pt={theme.spacing(3)}>
          <LoadingButton loading={loading} variant="outlined" color="primary" onClick={switchToForm}>
            {t('common.cancel')}
          </LoadingButton>
          <LoadingButton loading={loading} type="submit" variant="contained" color="primary">
            {t('auth.changePassword.title')}
          </LoadingButton>
        </Box>
      </Box>
    </Box>
  );
};
